import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { EmailInsightDto } from '../models/prospect';

/**
 * Mirror of EmailScanStatusSnapshot on the API. Timestamps come across as ISO
 * strings; the consumer renders them. Used by the /triage page's "Last scan:
 * HH:MM" banner so a silent scanner failure is visible instead of looking
 * identical to "no new mail".
 */
export interface EmailScanStatus {
  lastSuccessAt: string | null;
  lastAttemptAt: string | null;
  lastError: string | null;
  lastSkipReason: string | null;
  lastEmailsIngested: number | null;
  enabled: boolean;
}

@Injectable({ providedIn: 'root' })
export class InsightsService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl.replace(/\/+$/, '')}/api/email-insights`;

  private readonly _items = signal<EmailInsightDto[]>([]);
  readonly items = this._items.asReadonly();

  async load(includeDismissed = false): Promise<EmailInsightDto[]> {
    const params = new HttpParams().set('includeDismissed', String(includeDismissed));
    const list = await firstValueFrom(this.http.get<EmailInsightDto[]>(this.base, { params }));
    this._items.set(list);
    return list;
  }

  async scan(lookBackHours = 24): Promise<number> {
    const params = new HttpParams().set('lookBackHours', String(lookBackHours));
    return await firstValueFrom(this.http.post<number>(`${this.base}/scan`, null, { params }));
  }

  async scanStatus(): Promise<EmailScanStatus> {
    return await firstValueFrom(this.http.get<EmailScanStatus>(`${this.base}/status`));
  }

  async dismiss(id: string): Promise<void> {
    await firstValueFrom(this.http.post<void>(`${this.base}/${id}/dismiss`, null));
    this._items.update(list => list.filter(i => i.id !== id));
  }

  applyRealtime(insight: EmailInsightDto): void {
    this._items.update(list => [insight, ...list.filter(i => i.id !== insight.id)]);
  }

  applyDismissed(id: string): void {
    this._items.update(list => list.filter(i => i.id !== id));
  }
}
